<template>
    <div :data-overlay="navOverlayBanner">
        <component
            :is="bannerComponent"
            :entry="banner"
        />
    </div>
</template>

<script setup>
    const { query } = useGraphQL()
    const { digistormSettings, appId, globalSets, getGlobalSetField } = storeToRefs(useGlobalStore())
    const { entry } = storeToRefs(useEntryStore())

    const banner = ref(null)
    const gqlQuery = ref()

    const settings = computed(() => {
        const navigationSet = find(globalSets.value, { __typename: 'ds_Navigation_GlobalSet'})

        return {
            ...Object.entries(navigationSet).reduce((acc, [key]) => {
                acc[key] = getGlobalSetField.value('ds_Navigation_GlobalSet', key)
                return acc
            }, {})
        }
    })

    const navOverlayBanner = computed(() => {
        return !settings.value.mainNav_ShowBackground
    })

    const bannerType = computed(() => {
        return digistormSettings.value.bannerType
    })

    // check for any custom headers
    const customComponents = await import(`~/custom/${appId.value}/index.js`)
        .catch(() => {
            return null
        })

    if (customComponents?.default && bannerType.value === 'banner-custom') {
        gqlQuery.value = customComponents?.default?.HomeBannerQuery
    } else {
        // check the bannerType and dynamically import the query from queries/banners/{bannerType}.ts
        gqlQuery.value  = await import(`~/queries/banners/${bannerType.value}.ts`).then((q) => {
            return q.default
        })
            .catch(async () => {
                // fall back to the old bannerQuery for now
                return (await import('~/queries/banners/HomeBanner.ts')).default
            })
    }

    const getBannerData = async () => {
        if (!gqlQuery.value) {
            return []
        }

        gqlQuery.value = `
            ... on ${entry.value.__typename} {
                banner {
                    ${gqlQuery.value}
                }
            }
        `

        const bannerQuery = `
query pageContent($canonicalUid: String) {
    entry(uid: [$canonicalUid]) {
        ${gqlQuery.value}
    }
}`

        const bannerData = await query({
            key: 'home-banner',
            query: bannerQuery,
            variables: {
                canonicalUid: entry.value?.canonicalUid
            },
        })

        banner.value = get(bannerData, 'entry', [])
    }

    const bannerComponent = computed(() => {
        if (bannerType.value === 'banner-custom') {
            return customComponents?.default?.HomeBanner
        }

        return defineAsyncComponent(() =>
            import(`./home/${bannerType.value}/banner.async.vue`)
        )
    })

    await getBannerData()

</script>
